
import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { getAssessmentBySlug } from '@/services/assessmentService';
import { Assessment } from '@/types/assessment';
import FreeAssessment from './FreeAssessment';
import { useToast } from '@/hooks/use-toast';
import Logo from '@/icons/Logo';
import AssessmentLayout from '@/components/assessment-portal/AssessmentLayout';

const AssessmentPortal = () => {
  const { slug = '', mode = '' } = useParams();
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true);
        const data = await getAssessmentBySlug(slug);
        
        if (!data) {
          setError('Assessment not found');
          toast({
            variant: "destructive",
            title: "Error",
            description: "The requested assessment could not be found."
          });
          return;
        }
        
        if (!data.isActive) {
          setError('Assessment is not active');
          toast({
            variant: "destructive",
            title: "Assessment Unavailable",
            description: "This assessment is currently not active."
          });
          return;
        }
        
        setAssessment(data);
        
        // Update page title with the assessment name
        document.title = `Gapstars | ${data.title}`;
      } catch (err) {
        setError('Failed to load assessment');
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load the assessment. Please try again later."
        });
        console.error('Error fetching assessment:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAssessment();
  }, [slug, toast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted/30">
        <div className="flex items-center gap-3">
          <Logo className="h-8 w-8" />
          <span className="text-lg">Loading assessment...</span>
        </div>
      </div>
    );
  }

  if (error || !assessment) {
    return <Navigate to="/" />;
  }

  if (mode === 'free') {
    return <FreeAssessment assessment={assessment} />;
  }

  if (mode === 'premium') {
    // This would be implemented in the future
    // Provide required props to AssessmentLayout
    return (
      <AssessmentLayout 
        assessment={assessment}
        categories={[]} // Empty array since premium version not yet implemented
        currentCategoryIndex={0}
        currentStep="questions"
      >
        <div className="p-6">Premium assessment view not yet implemented.</div>
      </AssessmentLayout>
    );
  }

  return <Navigate to="/" />;
};

export default AssessmentPortal;
